import axios from 'axios'
import * as React from 'react'
import { useEffect, useState, useMemo } from 'react'
import HallSessions from './HallSessions'

const EVENT_DATES = [
  { dayNum: 0, date: '20th Aug | Wednesday' },
  { dayNum: 1, date: '21st Aug | Thursday' },
  { dayNum: 2, date: '22nd Aug | Friday' },
  { dayNum: 3, date: '23rd Aug | Saturday' },
]

export const DAY_0_HALLS: Array<ProgrammeSession['hall']> = [
  'Function Room 7',
  'Function Room 5',
  'Function Room 8',
  'Function Room 9',
  'Function Room 10',
  'Island Hospital',
]
export const DAY_1_HALLS: Array<ProgrammeSession['hall']> = [
  'Lecture Hall 1',
  'Lecture Hall 2',
  'Lecture Hall 3',
  'Function Room 7 (GM Floor)',
  'Function Room 5 & 6 (GM Floor)',
  'Function Room 8 (GM Floor)',
  'Function Room 4',
]
export const DAY_2_HALLS: Array<ProgrammeSession['hall']> = [
  'Lecture Hall 1',
  'Lecture Hall 2',
  'Lecture Hall 3',
  'Function Room 7 (GM Floor)',
  'Function Room 5 & 6 (GM Floor)',
  'Function Room 8 (GM Floor)',
  'Function Room 4',
]
export const DAY_3_HALLS: Array<ProgrammeSession['hall']> = [
  'Lecture Hall 1',
  'Lecture Hall 2',
]

export const FACULTY_TYPE = [
  'Chairperson',
  'Citation',
  'Co-Chair',
  'Emcee',
  'Judge',
  'Guest Of Honor',
  'Speaker',
  'Adjudicator',
  'Surgeon',
  'Panel',
  'Moderator',
  'Facilitator',
  'Facilitators',
  'Debate Team A',
  'Debate Team B',
] as const

export interface Faculty {
  id: number
  fullName: string
  avatar: string
  shortProfile: string
}

export interface FacultyWithType extends Faculty {
  facultyType: (typeof FACULTY_TYPE)[number]
}

export interface ProgrammeSession {
  id: number
  title: string
  desc?: string
  startAt: string
  endAt: string
  date: string // 11 JUN | SATURDAY
  time: string // 10:00 to 11:00
  day: 1 | 2 | 3
  hallOverwrite?: string
  hall:
    | 'Function Room 7'
    | 'Function Room 5'
    | 'Function Room 8'
    | 'Function Room 9'
    | 'Function Room 10'
    | 'Island Hospital'
    | 'Lecture Hall 1'
    | 'Lecture Hall 2'
    | 'Lecture Hall 3'
    | 'Function Room 4'
    | 'Function Room 5 & 6 (GM Floor)'
    | 'Function Room 7 (GM Floor)'
    | 'Function Room 8 (GM Floor)'
  faculties: Array<FacultyWithType>
  subsessions: Array<ProgrammeSubsession>
  secondsTillStart: number
  durationInSeconds: number
  ended?: boolean
}

export interface ProgrammeSubsession {
  id: number
  title?: string
  description?: string
  speakers: Array<Faculty>
  time: string // 10:00 to 11:00
}

interface ProgrammeProps {
  defaultDay?: number
}

const Programme: React.FC<ProgrammeProps> = ({ defaultDay = 1 }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [day, setDay] = useState<number>(defaultDay)
  const [data, setData] = useState<ProgrammeSession[]>([])
  const [activeHall, setActiveHall] = useState<ProgrammeSession['hall']>()
  const [searchQuery, setSearchQuery] = useState<string>('')

  const availableHalls = useMemo(() => {
    switch (day) {
      case 0:
        return DAY_0_HALLS
      case 1:
        return DAY_1_HALLS
      case 2:
        return DAY_2_HALLS
      case 3:
        return DAY_3_HALLS
      default:
        return []
    }
  }, [day])

  const COLORS = [
    '#c656a2',
    '#BF4069',
    '#369681',
    '#9C27B0',
    '#E67E22',
    '#2E86C1',
    '#16A085',
    '#D4AC0D',
    '#7D3C98',
    '#273746',
    '#943126',
    '#0E6655',
  ]

  const getTabColor = (hall: string) => {
    const hallIndex = availableHalls.findIndex((h) => h === hall)
    return COLORS[hallIndex % COLORS.length]
  }

  // Fetch data when day changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await axios.get(`/api/programme_sessions?day=${day}`)
        setData(response.data)
      } catch (error) {
        console.error('Error fetching programme sessions:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [day])

  // Set default active hall when day changes
  useEffect(() => {
    if (availableHalls.length > 0) {
      setActiveHall(availableHalls[0] as ProgrammeSession['hall'])
    }
  }, [availableHalls])

  const filteredData = useMemo(() => {
    if (!searchQuery) {
      return data
    }
    const lowerSearchQuery = searchQuery.toLowerCase()

    return data
      .map((session) => {
        const sessionTitle = session.title?.toLowerCase() || ''
        const sessionDesc = session.desc?.toLowerCase() || ''
        const sessionHall = session.hall?.toLowerCase() || ''

        const mainSessionMatches =
          sessionTitle.includes(lowerSearchQuery) ||
          sessionDesc.includes(lowerSearchQuery) ||
          sessionHall.includes(lowerSearchQuery) ||
          session.faculties.some((faculty) =>
            faculty.fullName.toLowerCase().includes(lowerSearchQuery)
          )

        const matchingSubsessions = session.subsessions.filter((subsession) => {
          const subsessionTitle = subsession.title?.toLowerCase() || ''
          const subsessionDesc = subsession.description?.toLowerCase() || ''
          return (
            subsessionTitle.includes(lowerSearchQuery) ||
            subsessionDesc.includes(lowerSearchQuery) ||
            subsession.speakers.some((speaker) =>
              speaker.fullName.toLowerCase().includes(lowerSearchQuery)
            )
          )
        })

        if (mainSessionMatches) {
          // If main session matches, return it with all its original subsessions
          return { ...session, subsessions: session.subsessions }
        } else if (matchingSubsessions.length > 0) {
          // If main session doesn't match, but subsessions do, return session with only matching subsessions
          return { ...session, subsessions: matchingSubsessions }
        }
        return null // Neither session nor its subsessions match
      })
      .filter((session): session is ProgrammeSession => session !== null)
  }, [data, searchQuery])

  // Get first programme for a specific hall
  const getFirstSessionForHall = (hall: string) => {
    const hallSessions = filteredData.filter(
      (programme: ProgrammeSession) => programme.hall === hall
    )
    // Return only the first session (earliest start time) for the hall
    return hallSessions.length > 0 ? [hallSessions[0]] : []
  }

  if (loading) {
    return (
      <div className="loader">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="pb-5" id="programmeAgenda">
      <div className="filterBtns">
        {/* Search Bar */}
        <div className="my-3 text-center">
          <input
            type="text"
            placeholder="Search by keyword..."
            className="form-control mx-auto"
            style={{ maxWidth: '400px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* 1st row - Date Tabs */}
        <div className="my-3 text-center">
          {EVENT_DATES.map(({ dayNum, date }) => (
            <button
              key={dayNum}
              className={`btn day-btn my-1 fw-bold me-2 ${
                dayNum === day ? 'active' : ''
              }`}
              onClick={() => setDay(dayNum)}
            >
              <div dangerouslySetInnerHTML={{ __html: date }}></div>
            </button>
          ))}
        </div>

        {/* 2nd row - Hall Tabs */}
        {availableHalls.length > 0 && (
          <div className="my-3 text-center">
            {availableHalls.map((hall) => (
              <button
                key={hall}
                className={`btn track-btn my-1 fw-bold me-2 ${
                  activeHall === hall ? 'active' : ''
                }`}
                onClick={() => setActiveHall(hall)}
              >
                {hall}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 3rd row - Session Cards */}
      {activeHall && (
        <div className="container">
          <div className="mt-4">
            {/* Single Hall Session */}
            <div className="d-flex justify-content-center">
              <div className="w-100" style={{ maxWidth: '80%' }}>
                <HallSessions
                  displayHallName={activeHall}
                  originalHallName={activeHall}
                  activeHall={activeHall}
                  programmes={getFirstSessionForHall(activeHall)}
                  getTabColor={getTabColor}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Programme
